import { useEffect, useRef, useState } from 'react';

import { useFilters } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { OMITTED_FIELDS } from 'Stores/SavedFiltersStore/SavedFilters.conf';
import {
	useCurrentFilter,
	useGetFilterIdByIndex,
	useSavedFilters,
	useSetCurrentFilter,
} from 'Stores/SavedFiltersStore/SavedFiltersStore';
import { getObjectIndex } from 'Utilities/helpers';

import { onSetCurrentFilterGA } from './SavedFilters.analytics';

interface UseCurrentSelectedFilter {
	currentFilter?: number;
	currentFilterIndex?: number;
}

export const useCurrentSelectedFilter = (): UseCurrentSelectedFilter => {
	const [currentSelectedFilterIndex, setCurrentSelectedFilterIndex] = useState<number | undefined>();
	const prevVehicleFiltersRef = useRef<unknown>(null);
	// rename useCurrentFilter to useCurrentFilterId later
	const currentFilterId = useCurrentFilter();
	const setCurrentFilter = useSetCurrentFilter();
	const vehicleFilters = useFilters();
	const savedFilters = useSavedFilters();
	const getFilterIdByIndex = useGetFilterIdByIndex();
	const savedFiltersData = savedFilters.map((filter) => filter.filterData);

	useEffect(() => {
		if (!savedFilters?.length) return;

		// Only react to vehicleFilters changes (user actions).
		// Ignore savedFilters-only changes (API refetch) to prevent race conditions
		// where createSavedFilter sets currentFilter, then refetch triggers deactivation.
		const isVehicleFiltersChange =
			prevVehicleFiltersRef.current !== null && prevVehicleFiltersRef.current !== vehicleFilters;
		const isFirstRun = prevVehicleFiltersRef.current === null;
		prevVehicleFiltersRef.current = vehicleFilters;

		if (!isFirstRun && !isVehicleFiltersChange) return;

		const currentFilterIndex = getObjectIndex({
			ignoreArrayOrder: true,
			ignoreFields: OMITTED_FIELDS,
			object: vehicleFilters as unknown as Record<string, unknown>,
			otherObjects: savedFiltersData as unknown as Record<string, unknown>[],
		});
		const hasCurrentFilter = currentFilterIndex > -1;
		const filterId = hasCurrentFilter ? getFilterIdByIndex(currentFilterIndex) : undefined;

		if (filterId !== currentFilterId) {
			setCurrentFilter(filterId);
			onSetCurrentFilterGA({
				currentFilter: filterId,
				prevFilter: currentFilterId,
				savedFilters,
			});
		}

		setCurrentSelectedFilterIndex(hasCurrentFilter ? currentFilterIndex : undefined);
	}, [vehicleFilters, savedFilters]);

	return {
		currentFilter: currentFilterId,
		currentFilterIndex: currentSelectedFilterIndex,
	};
};
